import { useUser } from "@/api/auth";
import { useCountNewOrdersSince } from "@/api/order";
import { useCountTrash } from "@/api/trash";
import logoInventoZen from "@/assets/images/InventoZen_Logo_Horizontal_SML.png";
import logoInventoZenWhite from "@/assets/images/InventoZen_Logo_Horizontal_White_SML.png";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { useLastTimeNewOrdersChecked } from "@/context/OrderContext";
import { HomeButton } from "./HomeButton";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  Building2,
  DownloadIcon,
  FileText,
  Globe,
  LogOut,
  Mail,
  Settings,
  ShoppingCart,
  Trash2,
  UploadIcon,
  UserCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getUserInitials,
  pickRandomAvatarColor,
  USER_AVATAR_COLOR_PREF_KEY,
} from "@/lib/user-avatar";
import {
  persistBoPreference,
  readBoPreference,
} from "@/lib/user-preferences/store";
import { cn, exportTableFromPath } from "@/lib/utils";
import { openTutorials } from "@/lib/open-tutorials";
import { EditProfileModale } from "../modal/EditProfileModal";
import NoCMSModel from "../modal/NoCMSModel";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { NotificationBadge } from "../ui/notification-badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";

/** Même style que le bouton Exporter de la fiche Essentiel (icône + libellé).
 *  `pt-2.5` laisse de l'air au-dessus du badge cercle (corbeille). */
const headerIconBtnClass =
  "h-auto flex-col gap-0 border-0 px-3 pt-2.5 pb-px text-xs font-medium text-gray-950 shadow-none";

const SUPPORT_EMAIL = "support@inventozen.com";

const DEFAULT_AVATAR_COLOR = "#7C3AED";

/** Pastille ronde colorée avec les initiales de l'utilisateur. */
const UserAvatar = ({
  initials,
  color,
}: {
  initials: string;
  color: string;
}) => (
  <span
    className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
    style={{ backgroundColor: color }}
    aria-hidden="true"
  >
    {initials}
  </span>
);

type HeaderProps = {
  /**
   * `overlay` : accueil du back-office, posé sur le fond choisi par
   * l'utilisateur — header en verre, teinte et opacité calculées selon le
   * fond pour rester lisible. Mêmes éléments.
   */
  variant?: "default" | "overlay";
  /** Mode overlay : verre sombre + texte et logo clairs, ou verre clair + logo couleur. */
  overlayTone?: "dark" | "light";
  /** Opacité du voile en mode overlay. */
  overlayScrim?: number;
};

const Header = ({
  variant = "default",
  overlayTone = "dark",
  overlayScrim = 0.5,
}: HeaderProps) => {
  const overlay = variant === "overlay";
  const darkOverlay = overlay && overlayTone === "dark";
  const iconBtnClass = cn(
    headerIconBtnClass,
    overlay && "bg-transparent",
    darkOverlay
      ? "text-[#fcfcfd] hover:bg-white/10"
      : overlay && "hover:bg-black/5",
  );
  const { logout, instanceData, token, boPreferencesReady } = useAuth();
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { data: userData } = useUser(token);
  const currentUser = userData?.[0];

  const initials = useMemo(
    () => getUserInitials(currentUser?.name, currentUser?.lastName),
    [currentUser?.name, currentUser?.lastName],
  );

  const [avatarColor, setAvatarColor] = useState(DEFAULT_AVATAR_COLOR);

  // Couleur tirée au hasard à la 1ʳᵉ connexion, puis stockée dans les réglages
  // utilisateur (UserConfig / préférences BO), pour rester stable ensuite.
  useEffect(() => {
    if (!boPreferencesReady) return;
    const stored = readBoPreference<string>(USER_AVATAR_COLOR_PREF_KEY);
    if (typeof stored === "string" && stored.length > 0) {
      setAvatarColor(stored);
      return;
    }
    const color = pickRandomAvatarColor();
    persistBoPreference(USER_AVATAR_COLOR_PREF_KEY, color);
    setAvatarColor(color);
  }, [boPreferencesReady]);

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showNoCMS, setShowNoCMS] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const exportableTable = exportTableFromPath(location.pathname);

  const { lastChecked } = useLastTimeNewOrdersChecked();
  const { data: newOrders } = useCountNewOrdersSince(lastChecked);
  const { data: countTrash } = useCountTrash({});

  const haveCMSId = instanceData?.CMS?.id;

  const handleClickCMS = () => {
    if (!haveCMSId) {
      setShowNoCMS(true);
      return;
    }
    if (instanceData?.CMS?.cmsAccessMode === "ADVANCED") {
      navigate("/cms/advanced/accueil");
      return;
    }
    navigate("/cms");
  };
  return (
    <div
      className={cn(
        "sticky top-0 z-50 w-full min-h-14 h-auto px-6 py-2 border-b border-slate-300 justify-between items-center inline-flex bg-white",
        overlay && "border-transparent bg-transparent backdrop-blur-[35px]",
        darkOverlay && "text-[#fcfcfd]",
      )}
      style={
        overlay
          ? {
              backgroundColor: darkOverlay
                ? `rgba(7, 7, 10, ${overlayScrim})`
                : `rgba(252, 252, 253, ${overlayScrim})`,
            }
          : undefined
      }
    >
      <EditProfileModale
        open={showEditProfile}
        onOpenChange={setShowEditProfile}
      />
      <NoCMSModel open={showNoCMS} onOpenChange={setShowNoCMS} />
      <div className="min-h-10 justify-start items-center gap-4 flex">
        <div className="w-32  relative">
          <img
            onClick={() => navigate("/accueil")}
            src={darkOverlay ? logoInventoZenWhite : logoInventoZen}
            alt={vt("bo.nav.logoAlt", "logoInventoZen")}
            className="w-full h-full cursor-pointer"
          />
        </div>
        {!overlay && <HomeButton />}
      </div>
      <Menubar
        className={cn(
          "justify-start items-center gap-2 flex border-0 shadow-none h-auto",
          overlay && "bg-transparent",
        )}
      >
        {/*
          Page « Tutoriels » du site vitrine : protégée par mot de passe
          WordPress, l'API fabrique l'URL qui le porte (cf. openTutorials).
        */}
        <Button
          onClick={() => void openTutorials()}
          className="font-['Raleway'] font-semibold"
        >
          {vt("bo.nav.tutorials", "Tutoriels")}
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/dashboard/notes")}
              className={iconBtnClass}
            >
              <FileText className="h-5 w-5" aria-hidden />
              {vt("bo.nav.notes", "Notes")}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" variant="action" className="px-3">
            <span className="text-white text-sm font-medium">
              {t("notes.tooltip")}
            </span>
          </TooltipContent>
        </Tooltip>
        {exportableTable != null && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  navigate("/export", {
                    state: { prevLocation: window.location.pathname },
                  })
                }
                className={iconBtnClass}
              >
                <DownloadIcon className="h-5 w-5" aria-hidden />
                {vt("bo.nav.export", "Exporter")}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" variant="action" className="px-3">
              <span className="text-white text-sm font-medium">
                {t("export.tooltip")}
              </span>
            </TooltipContent>
          </Tooltip>
        )}
        {/*
          Jumeau de l'export : meme garde sur la table courante, pour que
          l'import s'ouvre deja sur le bon type de fiche.
        */}
        {exportableTable != null && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  navigate(`/import?table=${exportableTable}`, {
                    state: { prevLocation: window.location.pathname },
                  })
                }
                className={iconBtnClass}
              >
                <UploadIcon className="h-5 w-5" aria-hidden />
                {vt("bo.nav.import", "Importer")}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" variant="action" className="px-3">
              <span className="text-white text-sm font-medium">
                {vt("bo.import.tooltip", "Importer un fichier Excel")}
              </span>
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={iconBtnClass}
              onClick={() => {
                window.location.href = `mailto:${SUPPORT_EMAIL}`;
              }}
            >
              <Mail className="h-5 w-5" aria-hidden />
              {vt("bo.nav.messaging", "Messagerie")}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" variant="action" className="px-3">
            <span className="text-white text-sm font-medium">
              {vt("bo.nav.messagingTooltip", SUPPORT_EMAIL)}
            </span>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/trash")}
              className={iconBtnClass}
            >
              <NotificationBadge
                notifNumber={countTrash ?? 0}
                className="bg-gray-500"
              >
                <Trash2 className="h-5 w-5" aria-hidden />
              </NotificationBadge>
              {vt("bo.nav.trash", "Corbeille")}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" variant="action" className="px-3">
            <span className="text-white text-sm font-medium">
              {vt("bo.nav.trashTooltip", "Corbeille")}{" "}
              <span
                className={
                  (countTrash ?? 0) === 0 ? "text-gray-400" : "text-white"
                }
              >
                ({countTrash ?? 0})
              </span>
            </span>
          </TooltipContent>
        </Tooltip>
        {/*<div className="p-2.5 rounded-lg justify-center items-center gap-1 flex">
          <HelpCircleIcon className="w-5 h-5" />
        </div>*/}
        <MenubarMenu>
          <MenubarTrigger className="px-2 py-1.5 rounded-lg justify-center items-center gap-1.5 flex">
            {newOrders && newOrders > 0 ? (
              <NotificationBadge notifNumber={newOrders}>
                <UserAvatar initials={initials} color={avatarColor} />
              </NotificationBadge>
            ) : (
              <UserAvatar initials={initials} color={avatarColor} />
            )}
            <ChevronDownIcon
              className={cn(
                "w-5 h-5 text-slate-600",
                darkOverlay && "text-[#fcfcfd]",
              )}
            />
          </MenubarTrigger>
          <MenubarContent>
            {/* Groupe compte : profil, compte principal, réglages */}
            <MenubarItem onSelect={() => setShowEditProfile(true)}>
              <UserCircle className="mr-3 h-4 w-4" aria-hidden />
              {t("userNavProfile")}
            </MenubarItem>
            <MenubarItem asChild>
              <Link to="/compte-principal">
                <Building2 className="mr-3 h-4 w-4" aria-hidden />
                {t("mainAccount")}
              </Link>
            </MenubarItem>
            <MenubarItem asChild>
              <Link to="/settings">
                <Settings className="mr-3 h-4 w-4" aria-hidden />
                {t("myAppSettings")}
              </Link>
            </MenubarItem>
            <MenubarSeparator className="my-1.5 bg-slate-300" />
            {/* Groupe vitrines : site web, e-shop */}
            <MenubarItem onSelect={handleClickCMS}>
              <Globe className="mr-3 h-4 w-4" aria-hidden />
              {t("userNavMySite")}
            </MenubarItem>
            {instanceData?.haveEshop == true && (
              <MenubarItem asChild className="flex flex-row justify-between">
                <Link to="/e-shop">
                  <span className="flex items-center">
                    <ShoppingCart className="mr-3 h-4 w-4" aria-hidden />
                    {t("userNavEshop")}
                  </span>
                  {newOrders != null && newOrders > 0 && (
                    <Badge className="p-1">{newOrders}</Badge>
                  )}
                </Link>
              </MenubarItem>
            )}
            <MenubarSeparator className="my-1.5 bg-slate-300" />
            <MenubarItem onClick={logout}>
              <LogOut className="mr-3 h-4 w-4" aria-hidden />
              {t("logout")}
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  );
};

export default Header;
